<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  strategy: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const form = ref({
  name: '',
  phone: '',
  text: '',
  group_link: '',
});

// Formata telefone do banco (5562992644843) para exibição ((62) 99264-4843)
const formatPhoneForDisplay = phone => {
  if (!phone) return '';
  // Remove 55 do início se existir e formata
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.substring(2);
  }
  if (digits.length === 11) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
  }
  return phone;
};

// Formata telefone para salvar no banco (apenas números)
const formatPhoneForDatabase = phone => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

// Máscara de telefone enquanto digita
const handlePhoneInput = event => {
  let value = event.target.value.replace(/\D/g, '');

  if (value.length > 11) {
    value = value.substring(0, 11);
  }

  if (value.length > 0) {
    if (value.length <= 2) {
      value = `(${value}`;
    } else if (value.length <= 7) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    } else {
      value = `(${value.substring(0, 2)}) ${value.substring(2, 7)}-${value.substring(7)}`;
    }
  }

  form.value.phone = value;
};

const isFormValid = computed(() => {
  return form.value.name?.trim().length > 0;
});

const dialogTitle = computed(() => {
  return props.isEditing
    ? t('STRATEGIES.DIALOG.EDIT_TITLE')
    : t('STRATEGIES.DIALOG.CREATE_TITLE');
});

watch(
  () => props.strategy,
  newStrategy => {
    if (newStrategy) {
      form.value = {
        name: newStrategy.name || '',
        phone: formatPhoneForDisplay(newStrategy.phone),
        text: newStrategy.text || '',
        group_link: newStrategy.group_link || '',
      };
    } else {
      form.value = {
        name: '',
        phone: '',
        text: '',
        group_link: '',
      };
    }
  },
  { immediate: true }
);

onMounted(() => {
  dialogRef.value?.open();
});

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  isSubmitting.value = true;

  const payload = {
    name: form.value.name,
    phone: formatPhoneForDatabase(form.value.phone),
    text: form.value.text,
    group_link: form.value.group_link || '',
  };

  try {
    if (props.isEditing && props.strategy) {
      await store.dispatch('strategies/updateStrategy', {
        id: props.strategy.id,
        ...payload,
      });
      useAlert(t('STRATEGIES.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('strategies/createStrategy', payload);
      useAlert(t('STRATEGIES.API.CREATE_SUCCESS'));
    }
    handleClose();
  } catch (error) {
    useAlert(t('STRATEGIES.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog ref="dialogRef" :title="dialogTitle" width="lg" @close="handleClose">
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="form.name"
        :label="t('STRATEGIES.FORM.NAME.LABEL')"
        :placeholder="t('STRATEGIES.FORM.NAME.PLACEHOLDER')"
      />

      <div class="relative flex flex-col min-w-0 gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('STRATEGIES.FORM.PHONE.LABEL') }}
        </label>
        <input
          :value="form.phone"
          type="text"
          :placeholder="t('STRATEGIES.FORM.PHONE.PLACEHOLDER')"
          class="block w-full h-10 px-3 py-2.5 text-sm rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand outline-offset-[-1px] border-none placeholder:text-n-slate-10 text-n-slate-12 transition-all duration-500 ease-in-out"
          @input="handlePhoneInput"
        />
      </div>

      <div>
        <label class="mb-1 text-sm font-medium text-n-slate-12 block">
          {{ t('STRATEGIES.FORM.TEXT.LABEL') }}
        </label>
        <TextArea
          v-model="form.text"
          :placeholder="t('STRATEGIES.FORM.TEXT.PLACEHOLDER')"
          min-height="5rem"
          max-height="10rem"
        />
      </div>

      <Input
        v-model="form.group_link"
        :label="t('STRATEGIES.FORM.GROUP_LINK.LABEL')"
        :placeholder="t('STRATEGIES.FORM.GROUP_LINK.PLACEHOLDER')"
      />
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('STRATEGIES.FORM.CANCEL')"
          class="bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
          @click="handleClose"
        />
        <Button
          :label="
            props.isEditing
              ? t('STRATEGIES.FORM.UPDATE')
              : t('STRATEGIES.FORM.CREATE')
          "
          :is-loading="isSubmitting"
          :disabled="!isFormValid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
